import React, { useEffect, useMemo, useCallback } from 'react';
import classNames from 'classnames';
import { ActionMeta, SingleValue } from 'react-select';

import { ICustomOptionProps } from 'common/components/FieldGenerator/types';

import SelectOption from 'common/components/Select/components/SelectOption';
import ErrorMessage from 'common/components/ErrorMessage';
import WindowedSelect from 'react-windowed-select';

import { OptionType } from './types';
import styles from './styles.module.scss';

export interface ISelectProps {
  label?: string,
  options: Array<OptionType>
  error?: string,
  placeholder?: string,
  required?: boolean,
  disabled?: boolean,
  isSearchable?: boolean,
  className?: string,
  value: OptionType,
  name: string,
  onChange?: (newValue: SingleValue<OptionType>, actionMeta: ActionMeta<OptionType>) => void,
  renderOptionLabelComponent?: () => (React.FC<ICustomOptionProps> | undefined),
}

const Select = ({
  label,
  options,
  error,
  required,
  disabled,
  isSearchable = false,
  className,
  placeholder = '',
  value,
  onChange,
  renderOptionLabelComponent,
  name,
}: ISelectProps) => {
  // Memoize the render component to avoid recreation on every render
  const RenderComponent = useMemo(
    () => renderOptionLabelComponent?.() || SelectOption,
    [renderOptionLabelComponent],
  );

  // Memoize the formatOptionLabel function to prevent unnecessary re-renders
  const formatOptionLabel = useCallback(
    (option: OptionType) => <RenderComponent option={option} />,
    [RenderComponent],
  );

  useEffect(() => {
    if (options.length === 1 && !value && !disabled) {
      const v: OptionType = options[0]!;
      const meta: ActionMeta<OptionType> = {
        action: 'select-option',
        option: v,
        name,
      };
      const timeout = setTimeout(() => {
        if (onChange) {
          onChange(v, meta);
        }
      });

      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [value, options, disabled]);
  return (
    <div
      className={classNames(
        styles.wrapper,
        'common-components-Select__wrapper',
        className,
        {
          [styles.disabled]: disabled,
          'common-components-Select__wrapper--disabled': disabled,
        },
      )}
    >
      {label && (
        <div className={classNames(styles.label, 'common-components-Select__label')}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </div>
      )}
      <WindowedSelect
        name={name}
        className={classNames(
          styles.select,
          'common-components-Select__select',
          {
            [styles.error]: error,
            'common-components-Select__select--error': error,
          },
        )}
        captureMenuScroll={false}
        placeholder={placeholder}
        isDisabled={disabled}
        isSearchable={isSearchable}
        classNamePrefix="select"
        getOptionLabel={(option: any) => option.title}
        getOptionValue={(option: any) => option.id}
        options={options}
        value={value}
        onChange={onChange as ((newValue: unknown, actionMeta: ActionMeta<unknown>) => void) | undefined}
        formatOptionLabel={formatOptionLabel as (data: unknown) => React.ReactNode}
        windowThreshold={100}
      />
      {error && <ErrorMessage text={error} />}
    </div>
  );
};

// @ts-ignore
export default Select;
